import { z } from 'zod';

/**
 * Game Instance Model
 *
 * A game instance is one independently-matchmakable game slot on a host
 * machine. A host running N game instances exposes N instances, each with its
 * own lifecycle, campaign, and player set. Identity is the pair
 * (machineId, gameInstanceIndex); the string form `${machineId}:${index}` is
 * used for Redis keys and matchmaking pool members.
 *
 * Contract frozen by docs/feature-plans/game-instance-v2/PHASE_1.md.
 */

export const gameInstanceIndexSchema = z.number().int().min(0).max(63);
export type GameInstanceIndex = z.infer<typeof gameInstanceIndexSchema>;

export const machineIdSchema = z
  .string()
  .min(1)
  .regex(/^[^:]+$/, 'machineId must not contain ":"');
// NOTE: no `MachineId` type alias here — `websocket/connection.ts` already
// exports one and the package barrel would collide on the name.

export type GameInstanceIdentity = {
  machineId: string;
  gameInstanceIndex: number;
};

/** `${machineId}:${gameInstanceIndex}` — string form for Redis keys and pool members. */
export const toGameInstanceId = (identity: GameInstanceIdentity): string =>
  `${identity.machineId}:${identity.gameInstanceIndex}`;

/**
 * Inverse of `toGameInstanceId`. Splits on the LAST ':' so the index suffix is
 * always recovered correctly; the machine id is still validated against
 * `machineIdSchema` (which forbids ':' altogether).
 *
 * @throws when the string is not a valid game instance id.
 */
export const parseGameInstanceId = (raw: string): GameInstanceIdentity => {
  const separatorIndex = raw.lastIndexOf(':');
  if (separatorIndex <= 0 || separatorIndex === raw.length - 1) {
    throw new Error(`Invalid game instance id: "${raw}"`);
  }
  const machineId = machineIdSchema.parse(raw.slice(0, separatorIndex));
  const gameInstanceIndex = gameInstanceIndexSchema.parse(Number(raw.slice(separatorIndex + 1)));
  return { machineId, gameInstanceIndex };
};

/**
 * Instance lifecycle (host-reported).
 * - idle:      nothing running, ready to launch any installed game
 * - launching: game/session spinning up, not yet ready for players
 * - ready:     game running and accepting players
 * - occupied:  in an active session with player(s)
 * - cooldown:  game running but matchmaking suppressed (e.g. campaign cooldown)
 */
export const gameInstanceLifecycleSchema = z.enum(['idle', 'launching', 'ready', 'occupied', 'cooldown']);
export type GameInstanceLifecycle = z.infer<typeof gameInstanceLifecycleSchema>;

/**
 * Host-authoritative report for one game instance. The host owns this data.
 */
export const gameInstanceHostReportSchema = z.object({
  campaignId: z.string().optional(),
  lifecycle: gameInstanceLifecycleSchema,
  acceptingMatches: z.boolean(),
  availabilityBlockReason: z.string().nullable().optional(),
  cooldownEndsAt: z.number().optional(),
  playerCount: z.number().int().nonnegative(),
  hostPeerId: z.string().optional(),
});
export type GameInstanceHostReport = z.infer<typeof gameInstanceHostReportSchema>;

/**
 * API-authoritative orchestration state for one game instance. The API owns
 * this data; hosts never send it.
 */
export const gameInstanceApiStateSchema = z.object({
  activeMatchRequestId: z.string().optional(),
  desiredCampaign: z.string().optional(),
  switchRequestedAt: z.number().optional(),
  lastMatchedAt: z.number().optional(),
});
export type GameInstanceApiState = z.infer<typeof gameInstanceApiStateSchema>;

/**
 * Full API-side record for one game instance: host report + API orchestration
 * state. Stored at realtime:matchmaking:v2:instance:{machineId}:{index}.
 */
export const gameInstanceRecordSchema = z.object({
  identity: z.object({
    machineId: machineIdSchema,
    gameInstanceIndex: gameInstanceIndexSchema,
  }),
  hostReported: gameInstanceHostReportSchema,
  apiManaged: gameInstanceApiStateSchema,
  lastUpdated: z.number(),
});
export type GameInstanceRecord = z.infer<typeof gameInstanceRecordSchema>;

/**
 * Machine-level record shared by all of a host's game instances. Stored at
 * realtime:matchmaking:v2:machine:{machineId}. Diagnostics and rewards
 * continue to index by machineId.
 */
export const machineRecordSchema = z.object({
  machineId: machineIdSchema,
  websocketId: z.string().optional(),
  geohash: z.string().optional(),
  coords: z.object({ lat: z.number(), lng: z.number() }).optional(),
  isCloudHost: z.boolean(),
  guestAccount: z.boolean(),
  gamingScore: z.number(),
  adminSessionActive: z.boolean(),
  // Machine capacity (android-multi-instance-host Phase 6, F7/G3). All
  // optional and additive: absent = never reported/never set.
  /** Host-reported effective instance ceiling (min of config, hardware, reported caps). */
  maxPlayers: z.number().int().positive().optional(),
  /** Host-reported count of pre-created VMs (android multi-instance). */
  provisionedInstanceCount: z.number().int().nonnegative().optional(),
  /** Host-reported instance ceiling the host clamps launches to. */
  maxGameInstances: z.number().int().positive().optional(),
  /** Ops-desired maxPlayers queued while the host was offline; cleared on apply. */
  desiredMaxPlayers: z.number().int().min(1).max(64).optional(),
  // Live occupancy rollup, computed by the coordinator on every host state
  // report (campaign-agnostic — does not depend on the campaign index). Absent
  // = never reported.
  /** Instances currently occupied (in a session / with players). */
  activeInstanceCount: z.number().int().nonnegative().optional(),
  /** Total instances the host reported this cycle. */
  totalInstanceCount: z.number().int().nonnegative().optional(),
});
export type MachineRecord = z.infer<typeof machineRecordSchema>;
